<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

import type { RatingData } from '@/types/products';

import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore.js';
import { convertUrlToResizer } from '@/utils/utils.js';

import TheStarRating from '../mode-list/TheStarRating.vue';
import Popover from './Popover.vue';

defineProps<{
  ratings: RatingData;
}>();

const { t } = useI18n();

const globalConfigurationStore = useGlobalConfigurationStore();
const { componentsBaseUrl } = storeToRefs(globalConfigurationStore);

function formatMonth(date: string): string {
  const [year, month] = date.split('-');

  const dateObj = new Date(Number(year), Number(month) - 1);

  return new Intl.DateTimeFormat('fr-FR', {
    month: 'long',
    year: 'numeric',
  }).format(dateObj);
}
</script>

<template>
  <section aria-label="google reviews">
    <h3>{{ t('rating.heading') }}</h3>
    <div class="rating_details">
      <div class="rating_note">
        <p class="rating">
          {{ ratings.rating }}
        </p>
        <TheStarRating :rating="ratings.rating * 2" star-size="24" />
        <span class="count">({{ ratings.ratingReviewCount }})</span>
        <img :src="`${componentsBaseUrl}/logo_google.jpg`" height="20" width="20" alt="google" loading="lazy">
      </div>
      <div>
        <Popover :title="t('rating.informations')" :content="t('rating.informations_content')">
          <template #button>
            <p class="rating_info">
              {{ t('rating.informations') }}
            </p>
          </template>
        </Popover>
      </div>
    </div>
    <article v-for="(review, index) in ratings.reviews" :key="index" class="review" :arial-label="`google review ${index + 1}`">
      <header class="header">
        <img :src="convertUrlToResizer(review.author_picture)" :alt="review.author_name" width="40" height="40" class="avatar">
        <div class="metadata">
          <p class="name">
            {{ review.author_name }}
          </p>
          <div class="reviews">
            <TheStarRating :rating="review.rating * 2" star-size="18" />
            <p v-if="review.date" class="date">
              {{ formatMonth(review.date) }}
            </p>
          </div>
        </div>
      </header>
      <main class="content">
        {{ review.text }}
      </main>
    </article>
  </section>
</template>

<style lang="css" scoped>
section {
  margin-top: 1rem;
  margin-bottom: 2rem;
}

section h3 {
  font-weight: 700;
  font-size: 24px;
  color: var(--yb-color-black);
  margin-bottom: 1rem;
}

.rating_details {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;

  @media screen and (width < 480px) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }

  .rating_note {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5rem;

    .rating {
      font-size: 18px;
      font-weight: 700;
    }

    .count {
      font-size: 14px;
      font-weight: 400;
    }
  }

  .rating_info {
    color: var(--yb-color-black);
    font-weight: 400;
    font-size: 14px;
    text-decoration: underline;
    cursor: pointer;
  }
}

.review {
  background-color: var(--yb-color-lightgray);
  border-radius: var(--yb-card-radius);
  margin-bottom: 1.5rem;
  padding: 1.5rem;

  .header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 1rem;
    gap: 1rem;

    .avatar {
      object-position: center center;
      object-fit: cover;
      border-radius: 9999px;
    }
    .metadata {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;

      .name {
        font-size: 1rem;
        font-weight: 700;
      }

      .reviews {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 0.5rem;

        .date {
          font-size: 12px;
          font-weight: 400;
        }
      }
    }

    .content {
      font-family: inherit;
      font-size: 1rem;
      font-weight: 400;
    }
  }
}
</style>
